import { Children, type ReactNode } from 'react';

import { isSlot } from '../../../../core/utils/is-slot.js';
import type { AnnotationsTooltipConfig } from '../../../core/components/annotations/components/tooltip/types/annotations-tooltip-props.js';
import type { AnnotationsMarkerConfig } from '../../../core/components/annotations/types/annotations-marker-props.js';
import type { AnnotationsTrackConfig } from '../../../core/components/annotations/types/annotations-track-props.js';
import type { AnnotationsActionsHandler } from '../../../core/components/annotations/types/annotations.js';
import { populateMissingMarkerProps } from '../../../core/components/annotations/utils/populate-missing-marker-props.js';
import { populateMissingTrackProps } from '../../../core/components/annotations/utils/populate-missing-track-props.js';
import type { TimeseriesChartAnnotationsProps } from '../../../timeseries/slots/annotations/Annotations.js';
import { Marker } from '../../../timeseries/slots/annotations/Marker.js';
import { AnnotationsTooltip } from '../../../timeseries/slots/annotations/Tooltip.js';
import { Track } from '../../../timeseries/slots/annotations/Track.js';

export type AnnotationsSlotsConfig = {
  tracks: AnnotationsTrackConfig[];
  showLabels: boolean;
  visibleTracksLimit: number | undefined;
  annotationsActions: AnnotationsActionsHandler | undefined;
  tooltip: AnnotationsTooltipConfig;
};

export const collectAnnotationsSlots = (
  annotationsProps: TimeseriesChartAnnotationsProps & { children?: ReactNode },
): AnnotationsSlotsConfig => {
  const {
    children,
    showLabels = true,
    visibleTracksLimit,
    annotationsActions,
    tooltip: tooltipProp,
  } = annotationsProps;

  const tracks: AnnotationsTrackConfig[] = [];
  let tooltip: AnnotationsTooltipConfig = tooltipProp ?? {
    template: undefined,
    hidden: false,
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, Track)) {
      const { children: trackChildren, ...trackProps } = child.props;
      const currentTrack = {
        ...trackProps,
        markers: [] as AnnotationsMarkerConfig[],
      };

      Children.forEach(trackChildren, (trackChild) => {
        if (isSlot(trackChild, Marker)) {
          const marker = populateMissingMarkerProps(
            trackChild.props,
            trackProps,
          );
          currentTrack.markers.push(marker);
        }
      });

      const track = populateMissingTrackProps(currentTrack);
      tracks.push(track);
    } else if (isSlot(child, AnnotationsTooltip)) {
      const { children: tooltipChildren, hidden } = child.props;
      tooltip = {
        template: tooltipChildren,
        hidden: hidden ?? false,
      };
    }
  });

  return {
    tracks,
    showLabels,
    visibleTracksLimit,
    annotationsActions,
    tooltip,
  };
};
